import PropTypes from 'prop-types';
import { ArrowLeft, RefreshCw } from 'react-feather';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import EstadoBadge from './EstadoBadge';
import TicketProps from './TicketProps';
import TicketThread from './TicketThread';
import TicketComposer from './TicketComposer';
import TicketHistorial from './TicketHistorial';
import {
  comentarioAMensaje,
  fileToBase64,
  PRIORIDADES,
  ticketsKeys,
  useActualizarCc,
  useActualizarTicket,
  useAgregarEtiqueta,
  useCrearComentario,
  useEliminarAdjunto,
  useEstados,
  useEtiquetas,
  useGrupos,
  useQuitarEtiqueta,
  useTemas,
  useTicketAdjuntos,
  useTicketComentarios,
  useTicketDetalle,
  useTicketHistorial,
  useTicketsUsuarios,
} from '../../features/tickets';

// Estados que cierran el ticket (Resuelto, Cerrado): exigen tema.
const ESTADOS_FINALES = [5, 6];
const AVISO_TEMA = 'Asigna un tema al ticket antes de resolverlo o cerrarlo';

const TicketDetail = ({ headquarter, id, onBack }) => {
  const { data: ticket, isLoading, isError } = useTicketDetalle(headquarter, id);
  const { data: usuarios = [] } = useTicketsUsuarios(headquarter);
  const { data: estados = [] } = useEstados(headquarter);
  const { data: grupos = [] } = useGrupos(headquarter);
  const { data: temas = [] } = useTemas(headquarter);
  const { data: etiquetasCatalogo = [] } = useEtiquetas(headquarter);
  const { data: comentarios = [], isLoading: cargandoComentarios } = useTicketComentarios(
    headquarter,
    id,
  );
  const { data: adjuntos = [] } = useTicketAdjuntos(headquarter, id);
  const { data: historial = [] } = useTicketHistorial(headquarter, id);

  const actualizar = useActualizarTicket(headquarter, id);
  const crearComentario = useCrearComentario(headquarter, id);
  const eliminarAdjunto = useEliminarAdjunto(headquarter, id);
  const agregarEtiqueta = useAgregarEtiqueta(headquarter, id);
  const quitarEtiqueta = useQuitarEtiqueta(headquarter, id);
  const actualizarCc = useActualizarCc(headquarter, id);

  // Todo cambio guardado desde la ficha confirma con un toast. Si falla, el
  // toast de error ya lo pone el HttpService (o el propio campo, en los
  // silent) y la mutación revierte el valor, así que aquí solo va el éxito.
  const nombreEn = (lista, valor, campo) =>
    lista.find((x) => String(x.id) === String(valor))?.[campo];

  const mensajeCambio = (campo, valor) => {
    switch (campo) {
      case 'estado': {
        const nombre = nombreEn(estados, valor, 'descripcion');
        return nombre ? `Estado cambiado a «${nombre}»` : 'Estado actualizado';
      }
      case 'grupo_id': {
        const nombre = nombreEn(grupos, valor, 'nombre');
        return nombre ? `Movido a la bandeja «${nombre}»` : 'Bandeja actualizada';
      }
      case 'prioridad': {
        const nombre = PRIORIDADES[valor]?.label;
        return nombre ? `Prioridad cambiada a «${nombre}»` : 'Prioridad actualizada';
      }
      case 'tema_id': {
        if (valor === null) return 'Tema quitado';
        const nombre = nombreEn(temas, valor, 'nombre');
        return nombre ? `Tema cambiado a «${nombre}»` : 'Tema actualizado';
      }
      default:
        return 'Ticket actualizado';
    }
  };

  const onChange = (campo, valor) => {
    // Al reasignar, el backend avisa por correo al nuevo agente y devuelve
    // `avisoAsignacion` ('enviado' | 'omitido' | 'error'). 'omitido' (p. ej. te
    // lo asignas a ti mismo) confirma la asignación sin hablar del correo.
    if (campo === 'agente') {
      const nombre = nombreEn(usuarios, valor, 'nombre') ?? 'el agente';
      actualizar.mutate(
        { agente: valor },
        {
          onSuccess: (resp) => {
            if (valor === null) {
              toast.success('Ticket sin asignar');
            } else if (resp?.avisoAsignacion === 'enviado') {
              toast.success(`Asignado a ${nombre}. Se le ha avisado por correo.`);
            } else if (resp?.avisoAsignacion === 'error') {
              toast.warning(`Asignado a ${nombre}, pero no se pudo enviar el aviso por correo.`);
            } else {
              toast.success(`Asignado a ${nombre}`);
            }
          },
        },
      );
      return;
    }
    if (campo === 'estado' && ESTADOS_FINALES.includes(Number(valor)) && !ticket?.tema_id) {
      toast.warning(AVISO_TEMA);
      return;
    }
    actualizar.mutate(
      { [campo]: valor },
      { onSuccess: () => toast.success(mensajeCambio(campo, valor)) },
    );
  };

  const onAddEtiqueta = (payload) => {
    const nombre = payload.nombre ?? nombreEn(etiquetasCatalogo, payload.etiquetaId, 'nombre');
    agregarEtiqueta.mutate(payload, {
      onSuccess: () => toast.success(nombre ? `Etiqueta «${nombre}» añadida` : 'Etiqueta añadida'),
    });
  };

  const onRemoveEtiqueta = (etiquetaId) => {
    const nombre = nombreEn(ticket?.etiquetas || [], etiquetaId, 'nombre');
    quitarEtiqueta.mutate(etiquetaId, {
      onSuccess: () => toast.success(nombre ? `Etiqueta «${nombre}» quitada` : 'Etiqueta quitada'),
    });
  };

  const onEliminarAdjunto = (adjId) =>
    eliminarAdjunto.mutate(adjId, { onSuccess: () => toast.success('Adjunto eliminado') });

  const queryClient = useQueryClient();
  // El prefijo ['tickets','detalle',hq,id] cubre detalle, comentarios, adjuntos e historial.
  const refrescando = useIsFetching({ queryKey: ticketsKeys.detalle(headquarter, id) }) > 0;
  const refrescar = () =>
    queryClient.invalidateQueries({ queryKey: ticketsKeys.detalle(headquarter, id) });

  const enviando = crearComentario.isPending || actualizar.isPending;

  // CC del ticket: el editor del composer guarda la lista entera (y pinta los
  // 400 del backend); desde el hilo se añade una dirección del Para/CC de un
  // correo, y solo se ofrecen candidatas válidas (esCandidatoCc).
  const ccTicket = ticket?.cc || [];
  const cambiarCc = async (emails) => {
    const res = await actualizarCc.mutateAsync(emails);
    toast.success('CC actualizado');
    return res;
  };
  const anadirCc = (email) => {
    if (ccTicket.includes(email)) return;
    actualizarCc.mutate([...ccTicket, email], {
      onSuccess: () => toast.success(`${email} añadido al CC`),
    });
  };

  const onSend = async ({ modo, texto, estadoId, archivos }) => {
    const cuerpo = (texto || '').trim();
    if (!cuerpo && archivos.length === 0) return false;
    if (modo === 'publica' && ESTADOS_FINALES.includes(Number(estadoId)) && !ticket?.tema_id) {
      toast.warning(AVISO_TEMA);
      return false;
    }

    // Adjuntos INLINE (base64) en el propio comentario: solo así el backend los
    // adjunta al email del solicitante en respuestas públicas de agente. El
    // flujo de dos pasos (POST /tickets/:id/adjuntos) solo guardaba en BD/GCS y
    // el fichero no llegaba al correo del cliente.
    const adjuntosPayload = await Promise.all(
      archivos.map(async (file) => ({
        nombre: file.name,
        tipo_mime: file.type,
        contenido_base64: await fileToBase64(file),
      })),
    );

    const res = await crearComentario.mutateAsync({
      cuerpo: cuerpo || 'Adjunto',
      es_publico: modo === 'publica',
      ...(adjuntosPayload.length ? { adjuntos: adjuntosPayload } : {}),
    });
    if (!res || !res.id) return false;

    if (modo !== 'publica') {
      toast.success('Nota interna guardada');
      return true;
    }

    // Cambio de estado al enviar (solo respuesta pública).
    if (estadoId && estadoId !== ticket.estado) {
      await actualizar.mutateAsync({ estado: estadoId });
      const nombre = nombreEn(estados, estadoId, 'descripcion');
      toast.success(
        nombre ? `Respuesta enviada y estado cambiado a «${nombre}»` : 'Respuesta enviada',
      );
    } else {
      toast.success('Respuesta enviada');
    }

    return true;
  };

  const cabecera = (
    <header className="tickets-detail__head">
      <button type="button" className="tickets-btn tickets-btn--ghost" onClick={onBack}>
        <ArrowLeft size={16} aria-hidden="true" />
        Volver
      </button>
      <span className="tickets-detail__id">#{id}</span>
      {ticket && <h1 className="tickets-detail__asunto">{ticket.asunto}</h1>}
      {ticket && <EstadoBadge estadoId={ticket.estado} />}
      <button
        type="button"
        className="tickets-btn tickets-btn--secondary tickets-btn--icon tickets-detail__refresh"
        onClick={refrescar}
        disabled={refrescando}
        aria-label="Refrescar ticket"
        title="Refrescar"
      >
        <RefreshCw size={16} aria-hidden="true" className={refrescando ? 'tickets-spin' : ''} />
      </button>
    </header>
  );

  if (isLoading) {
    return (
      <section className="tickets-panel tickets-detail">
        {cabecera}
        <div className="tickets-empty">Cargando ticket…</div>
      </section>
    );
  }

  if (isError || !ticket) {
    return (
      <section className="tickets-panel tickets-detail">
        {cabecera}
        <div className="tickets-empty">No se pudo cargar el ticket.</div>
      </section>
    );
  }

  return (
    <section className="tickets-panel tickets-detail">
      {cabecera}

      <div className="tickets-detail__grid">
        <div className="tickets-detail__main">
          {/* Responder arriba (como Zendesk/Freshdesk): sin bajar al final de una conversación larga. */}
          <TicketComposer
            estadoActualId={ticket.estado}
            estados={estados}
            onSend={onSend}
            enviando={enviando}
            headquarter={headquarter}
            ticketId={id}
            solicitante={ticket.solicitante_email}
            cc={ccTicket}
            onChangeCc={cambiarCc}
            ccGuardando={actualizarCc.isPending}
          />
          {cargandoComentarios ? (
            <div className="tickets-empty">Cargando conversación…</div>
          ) : (
            <TicketThread
              mensajes={comentarios.map(comentarioAMensaje)}
              adjuntos={adjuntos}
              onEliminarAdjunto={onEliminarAdjunto}
              solicitante={ticket.solicitante_email}
              cc={ccTicket}
              onAddCc={anadirCc}
              recientesPrimero
              plegarAnteriores
            />
          )}
          <TicketHistorial entradas={historial} />
        </div>
        <TicketProps
          ticket={ticket}
          usuarios={usuarios}
          estados={estados}
          grupos={grupos}
          temas={temas}
          etiquetasCatalogo={etiquetasCatalogo}
          onChange={onChange}
          onAddEtiqueta={onAddEtiqueta}
          onRemoveEtiqueta={onRemoveEtiqueta}
        />
      </div>
    </section>
  );
};

TicketDetail.propTypes = {
  headquarter: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onBack: PropTypes.func.isRequired,
};

export default TicketDetail;
